/**
 * Geometry helpers for the House Map (all coordinates in meters).
 */

// Ray-casting point-in-polygon test.
export function pointInPolygon(pt, polygon) {
  let inside = false;
  const n = polygon.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y;
    const xj = polygon[j].x, yj = polygon[j].y;
    const intersect =
      yi > pt.y !== yj > pt.y &&
      pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

// Return the id of the first room whose polygon contains (x, y), or null.
export function roomAt(x, y, rooms) {
  for (const room of rooms || []) {
    if (room.polygon && pointInPolygon({ x, y }, room.polygon)) return room.id;
    }
  return null;
}

// Smallest signed difference between two angles (degrees), result in (-180, 180].
function angleDiff(a, b) {
  let d = ((a - b + 180) % 360) - 180;
  if (d <= -180) d += 360;
  return d;
}

/**
 * Is point `pt` inside a sensor cone?
 * origin: {x,y}, rotation: aim in degrees (0 = +X / right), fovAngle: full cone angle,
 * rangeM: max distance. Returns true if within range and within ±fov/2 of the aim.
 */
export function inCone(pt, origin, rotation, fovAngle, rangeM) {
  const dx = pt.x - origin.x;
  const dy = pt.y - origin.y;
  const dist = Math.hypot(dx, dy);
  if (dist > rangeM || dist < 1e-6) return dist < 1e-6; // origin counts as covered
  const ang = (Math.atan2(dy, dx) * 180) / Math.PI;
  return Math.abs(angleDiff(ang, rotation)) <= fovAngle / 2;
}

// Is a point covered by ANY sensor of ANY node?
export function coveredByAny(pt, nodes) {
  for (const node of nodes || []) {
    for (const s of node.sensors || []) {
      if (inCone(pt, node, s.rotation, s.fovAngle, s.rangeM)) return true;
    }
  }
  return false;
}

/**
 * Is a point covered by the RELIABLE (inner) ring of any sensor?
 * The STHS34PF80 only reliably detects a stationary/seated person within ~2 m,
 * so dwell zones (resting furniture) must be checked against `reliableM`, not the
 * full `rangeM` presence cone.
 */
export function coveredByReliable(pt, nodes) {
  for (const node of nodes || []) {
    for (const s of node.sensors || []) {
      const r = s.reliableM ?? s.rangeM;
      if (inCone(pt, node, s.rotation, s.fovAngle, r)) return true;
    }
  }
  return false;
}

// Center point of an axis-aligned dwell-zone rectangle.
export function dwellCenter(z) {
  return { x: z.x + (z.w || 0) / 2, y: z.y + (z.h || 0) / 2 };
}

/**
 * Fraction (0..1) of dwell zones whose center sits inside a sensor's reliable ring.
 * Returns null when there are no dwell zones.
 */
export function dwellCoverage(dwellZones, nodes) {
  const zones = dwellZones || [];
  if (zones.length === 0) return null;
  let covered = 0;
  for (const z of zones) {
    if (coveredByReliable(dwellCenter(z), nodes)) covered++;
  }
  return covered / zones.length;
}

// Sample a polyline path into points spaced ~`step` meters apart.
export function samplePath(points, step = 0.2) {
  const out = [];
  if (!points || points.length === 0) return out;
  if (points.length === 1) return [{ ...points[0] }];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i], b = points[i + 1];
    const segLen = Math.hypot(b.x - a.x, b.y - a.y);
    const n = Math.max(1, Math.round(segLen / step));
    for (let k = 0; k < n; k++) {
      const t = k / n;
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    }
  }
  out.push({ ...points[points.length - 1] });
  return out;
}

// Fraction (0..1) of a path's sampled points covered by the given nodes' sensors.
export function pathCoverage(points, nodes, step = 0.2) {
  const samples = samplePath(points, step);
  if (samples.length === 0) return 0;
  let covered = 0;
  for (const p of samples) if (coveredByAny(p, nodes)) covered++;
  return covered / samples.length;
}

/**
 * Walk a sampled path and return the distance (meters) covered before the first
 * sample that falls inside a sensor cone — the "dark lead-in".
 *   samples: ordered points, covered: matching boolean array.
 *   fromStart=false walks the path in reverse (the other travel direction).
 * Returns 0 if lit from the first step, or null if never detected.
 */
export function leadInDistance(samples, covered, fromStart = true) {
  if (!samples || samples.length === 0) return null;
  const order = fromStart
    ? samples.map((_, i) => i)
    : samples.map((_, i) => i).reverse();
  let dist = 0;
  for (let k = 0; k < order.length; k++) {
    const i = order[k];
    if (covered[i]) return Math.round(dist * 100) / 100;
    if (k < order.length - 1) {
      const j = order[k + 1];
      dist += Math.hypot(samples[j].x - samples[i].x, samples[j].y - samples[i].y);
    }
  }
  return null;
}

/**
 * Classify every sampled point of a path for rendering:
 *   "covered" — inside a sensor cone (light fires immediately)
 *   "leadin"  — blind, but before the first detection in a travel direction
 *               (you walk in the dark before being seen; tolerable thanks to the
 *               1-minute cooldown once detected)
 *   "blind"   — blind gap after detection has already started (genuinely dark)
 * For bidirectional paths the lead-in applies at BOTH ends.
 * Returns { covered: boolean[], classes: string[], firstCovered, lastCovered }.
 */
export function classifyPathSamples(samples, nodes, bidirectional = true) {
  const covered = samples.map((p) => coveredByAny(p, nodes));
  let firstCovered = -1;
  let lastCovered = -1;
  for (let i = 0; i < covered.length; i++) {
    if (covered[i]) {
      if (firstCovered === -1) firstCovered = i;
      lastCovered = i;
    }
  }
  const classes = covered.map((c, i) => {
    if (c) return "covered";
    if (firstCovered === -1) return "blind"; // never covered at all
    if (i < firstCovered) return "leadin";
    if (bidirectional && i > lastCovered) return "leadin";
    return "blind";
  });
  return { covered, classes, firstCovered, lastCovered };
}

/**
 * Suggest a node placement (position + single sensor aim) that maximizes coverage of `path`.
 * Searches candidate positions along the room's polygon edges (walls) and aim angles.
 * Returns { x, y, rotation, coverage } or null.
 */
export function suggestPlacement(path, room, fovAngle = 80, rangeM = 4) {
  if (!path || path.length < 1 || !room || !room.polygon) return null;
  const poly = room.polygon;
  const candidates = [];
  // Sample positions slightly inside each wall (offset toward centroid).
  let cx = 0, cy = 0;
  for (const p of poly) { cx += p.x; cy += p.y; }
  cx /= poly.length; cy /= poly.length;
  const inset = 0.15; // meters off the wall

  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const segLen = Math.hypot(b.x - a.x, b.y - a.y);
    const steps = Math.max(2, Math.round(segLen / 0.5));
    for (let k = 0; k <= steps; k++) {
      const t = k / steps;
      let px = a.x + (b.x - a.x) * t;
      let py = a.y + (b.y - a.y) * t;
      // Nudge toward centroid so the cone fires into the room.
      const len = Math.hypot(cx - px, cy - py) || 1;
      px += ((cx - px) / len) * inset;
      py += ((cy - py) / len) * inset;
      candidates.push({ x: Math.round(px * 10) / 10, y: Math.round(py * 10) / 10 });
    }
  }

  const samples = samplePath(path, 0.2);
  if (samples.length === 0) return null;

  let best = null;
  for (const c of candidates) {
    for (let rot = 0; rot < 360; rot += 10) {
      let covered = 0;
      for (const p of samples) {
        if (inCone(p, c, rot, fovAngle, rangeM)) covered++;
      }
      const cov = covered / samples.length;
      if (!best || cov > best.coverage) {
        best = { x: c.x, y: c.y, rotation: rot, coverage: cov };
      }
    }
  }
  return best;
}
